import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from './store.models';
import { API_BASE } from './ordering-api';
import { CustomerAddress, ReverseGeocode, SaveAddressInput } from './address.models';

/**
 * The signed-in customer's saved delivery addresses, stored server-side in CRM (a ContactAddress on
 * the customer's per-company contact). Writes carry the storeId so the API resolves the
 * company/contact. The customer JWT is attached by customerAuthInterceptor.
 */
@Injectable({ providedIn: 'root' })
export class AddressService {
  private readonly http = inject(HttpClient);
  private readonly base = `${API_BASE}/api/public/addresses`;

  /**
   * The saved addresses. With a storeId: exactly the book held for that store's company — what
   * checkout there ships from. Without one (the marketplace location picker, /account): the whole
   * book across every company the customer has saved an address with, one per Home/Work/Other slot.
   */
  list(storeId?: string | null): Observable<ApiResponse<CustomerAddress[]>> {
    const query = storeId ? `?storeId=${storeId}` : '';
    return this.http.get<ApiResponse<CustomerAddress[]>>(`${this.base}${query}`);
  }

  create(input: SaveAddressInput): Observable<ApiResponse<CustomerAddress>> {
    return this.http.post<ApiResponse<CustomerAddress>>(this.base, input);
  }

  update(id: string, input: SaveAddressInput): Observable<ApiResponse<CustomerAddress>> {
    return this.http.put<ApiResponse<CustomerAddress>>(`${this.base}/${id}`, input);
  }

  setDefault(id: string, storeId?: string | null): Observable<ApiResponse<CustomerAddress>> {
    return this.http.put<ApiResponse<CustomerAddress>>(`${this.base}/${id}/default`, { storeId: storeId ?? null });
  }

  /** Delete a saved address. No storeId needed — the API takes the owning company from the row. */
  remove(id: string): Observable<ApiResponse<boolean>> {
    return this.http.delete<ApiResponse<boolean>>(`${this.base}/${id}`);
  }

  reverseGeocode(lat: number, lng: number): Observable<ApiResponse<ReverseGeocode | null>> {
    return this.http.get<ApiResponse<ReverseGeocode | null>>(`${this.base}/reverse-geocode?lat=${lat}&lng=${lng}`);
  }
}
